/**
 * PwaPrompts -- "Add to Home Screen" install banner + web-push opt-in.
 *
 * - Captures `beforeinstallprompt` and offers an Install button.
 * - "Enable alerts" requests Notification permission, subscribes to push with
 *   the HMS VAPID key (/push/vapid-public-key/) and registers the subscription
 *   (/push/subscribe/). The service worker already shows the pushed alerts.
 * Hidden once installed (standalone) + alerts granted, or when dismissed.
 */
import { Bell, Download, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";

import { getAuthorizationHeader } from "@/Utils/request/utils";

function authHeaders(): Record<string, string> {
  const a = getAuthorizationHeader();
  return a ? { Authorization: a } : {};
}

function urlB64ToUint8(b64: string): Uint8Array {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from(Array.from(raw).map((c) => c.charCodeAt(0)));
}

export default function PwaPrompts() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [installEvt, setInstallEvt] = useState<any>(null);
  const [notifState, setNotifState] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "denied",
  );
  const [dismissed, setDismissed] = useState(
    typeof localStorage !== "undefined" &&
      localStorage.getItem("pwa_prompt_dismissed") === "1",
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onPrompt = (e: any) => {
      e.preventDefault();
      setInstallEvt(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    await installEvt.userChoice;
    setInstallEvt(null);
  };

  const enableAlerts = async () => {
    if (typeof Notification === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setNotifState(perm);
      if (perm !== "granted") return;
      const reg = await navigator.serviceWorker.ready;
      const r = await fetch("/api/hmims/v1/push/vapid-public-key/", {
        headers: authHeaders(),
        credentials: "omit",
      });
      const { publicKey } = await r.json();
      if (!publicKey) return;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8(publicKey),
      });
      await fetch("/api/hmims/v1/push/subscribe/", {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ subscription: sub.toJSON() }),
      });
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem("pwa_prompt_dismissed", "1");
    } catch {
      /* ignore */
    }
  };

  const standalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (navigator as any).standalone === true);

  const canInstall = !!installEvt;
  const canEnable = notifState !== "granted" && notifState !== "denied";

  if (dismissed || (standalone && !canEnable) || (!canInstall && !canEnable)) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-1/2 z-[60] w-[92%] max-w-md -translate-x-1/2 rounded-xl border border-blue-200 bg-white p-3 shadow-lg">
      <div className="flex items-start gap-2">
        <div className="flex-1 text-sm">
          <div className="font-semibold text-blue-800">SEAES HMS</div>
          <div className="text-gray-600">
            {canInstall && canEnable
              ? "Install the app and turn on alerts for critical notifications."
              : canInstall
                ? "Install SEAES HMS for quick, full-screen access."
                : "Turn on alerts for low stock, panic labs and other critical events."}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {canInstall && (
              <Button size="sm" onClick={install}>
                <Download className="mr-1 size-4" /> Install
              </Button>
            )}
            {canEnable && (
              <Button
                size="sm"
                variant="outline"
                onClick={enableAlerts}
                disabled={busy}
              >
                <Bell className="mr-1 size-4" /> Enable alerts
              </Button>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
